import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  ViewChild,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';
import { TranslatePipe } from '@ngx-translate/core';

import {
  AiAssistantUiContext,
  AiAssistantUiContextKey,
  stripAiAssistantUiContextParts,
} from '@domain/ai-assistant/ai-assistant-ui-context';

import { appSend24Icon } from '@icons/send-24';

import { AiUiContextBoxComponent } from '../ai-ui-context-box/ai-ui-context-box.component';

export interface AiComposerMessage {
  message: string;
  uiContext?: AiAssistantUiContext;
}

@Component({
  selector: 'app-ai-composer',
  imports: [FormsModule, SvgIconComponent, TranslatePipe, AiUiContextBoxComponent],
  templateUrl: './ai-composer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col relative z-0' },
})
export class AiComposerComponent {
  @ViewChild('textarea') private readonly textareaRef?: ElementRef<HTMLTextAreaElement>;

  readonly #injector = inject(Injector);
  #isInternalChange = false;
  #contextIdentity = '';
  #wasDisabled = false;

  public disabled = input(false);
  public uiContext = input<AiAssistantUiContext | undefined>();
  public messageSent = output<AiComposerMessage>();

  public value = model<string>('');
  public readonly removedKeys = signal<ReadonlySet<AiAssistantUiContextKey>>(new Set());
  public readonly effectiveUiContext = computed(() => stripAiAssistantUiContextParts(this.uiContext(), this.removedKeys()));

  constructor() {
    injectRegisterIcons([appSend24Icon]);
    effect(
      () => {
        const contextIdentity = this.getContextIdentity(this.uiContext());
        if (contextIdentity !== this.#contextIdentity) {
          this.#contextIdentity = contextIdentity;
          this.removedKeys.set(new Set());
        }
      },
      {
        allowSignalWrites: true,
      }
    );

    effect(() => {
      this.value(); // track
      if (this.#isInternalChange) {
        this.#isInternalChange = false;
        return;
      }
      // External change (e.g. suggestion prefill) — adjust height after the DOM reflects the new value
      afterNextRender(() => this.adjustHeight(), { injector: this.#injector });
    });

    afterNextRender(() => this.#focusTextarea(), { injector: this.#injector });

    effect(() => {
      const disabled = this.disabled();
      if (this.#wasDisabled && !disabled) {
        afterNextRender(() => this.#focusTextarea(), { injector: this.#injector });
      }
      this.#wasDisabled = disabled;
    });
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.submit();
    }
  }

  public onSubmit(): void {
    this.submit();
  }

  public onRemoveContext(key: AiAssistantUiContextKey): void {
    this.removedKeys.update((keys) => new Set(keys).add(key));
  }

  public onInput(): void {
    // Mark as internal so the effect skips (already handled here)
    this.#isInternalChange = true;
    this.adjustHeight();
  }

  private submit(): void {
    const message = this.value().trim();
    if (!message || this.disabled()) return;
    this.messageSent.emit({ message, uiContext: this.effectiveUiContext() });
    this.value.set('');
  }

  #focusTextarea(): void {
    const el = this.textareaRef?.nativeElement;
    if (!el || this.disabled()) return;
    el.focus();
  }

  private adjustHeight(): void {
    const el = this.textareaRef?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  private getContextIdentity(context?: AiAssistantUiContext): string {
    return [context?.siteId, context?.chargerId, context?.evseId, context?.vehicleId].join('|');
  }
}
